import { api } from "./api";
import { formatPrice, platformLabel } from "./format";

/**
 * Everything the Browse page does that is not JSX.
 *
 * Unlike Saved and Dismissed, the filtering is NOT here: it runs on the server.
 * The full listings set serializes to roughly a megabyte, too much to ship
 * per page load to make a slider feel instant, where Dismissed's set is
 * small enough to send whole.
 *
 * So what lives here is the query the server needs, the paging state machine,
 * and the formatting the cards do.
 */

/** Mirrors `Listing` in `server/routers/listings.py`. */
export interface Listing {
  key: string;
  title: string;
  artist: string;
  platform: string;
  url: string;
  image_url: string;
  price: number | null;
  type: string;
  size: string;
  color: string;
  first_seen: string;
  last_seen: string;
  is_new: boolean;
  saved: boolean;
  discount: number | null;
  baseline: number | null;
  days_since_seen: number | null;
}

/** Mirrors `Facets`. Built from the visible set BEFORE the user's filters, so
 * narrowing to one artist never removes the others from the dropdown. */
export interface Facets {
  artists: string[];
  types: string[];
  sizes: string[];
  colors: string[];
  platforms: string[];
  price_min: number | null;
  price_max: number | null;
}

/** Mirrors `ListingsPage`. */
export interface ListingsPage {
  items: Listing[];
  total: number;
  offset: number;
  limit: number;
  facets: Facets;
  dismissed_count: number;
  saved_count: number;
}

export type Sort = "newest" | "oldest" | "artist" | "price_low" | "price_high";

/** The five orderings and their labels. Kept as a list so the dropdown and
 * the type cannot disagree about what exists. */
export const SORTS: ReadonlyArray<{ value: Sort; label: string }> = [
  { value: "newest", label: "Newest first" },
  { value: "artist", label: "Artist A-Z" },
  { value: "price_low", label: "Price low-high" },
  { value: "price_high", label: "Price high-low" },
  { value: "oldest", label: "Oldest first" },
];

export interface BrowseFilters {
  artists: string[];
  platforms: string[];
  types: string[];
  sizes: string[];
  colors: string[];
  priceMin: number | null;
  priceMax: number | null;
  dealsOnly: boolean;
  newOn: string | null;
  sort: Sort;
}

export const DEFAULT_FILTERS: BrowseFilters = {
  artists: [],
  platforms: [],
  types: [],
  sizes: [],
  colors: [],
  priceMin: null,
  priceMax: null,
  dealsOnly: false,
  newOn: null,
  sort: "newest",
};

/** Read the dated email deep link. Invalid dates fall back to the normal feed. */
export function filtersFromSearch(search: string): BrowseFilters {
  const raw = new URLSearchParams(search).get("new_on");
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return { ...DEFAULT_FILTERS };

  const [year, month, day] = raw.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  const valid = parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
  return { ...DEFAULT_FILTERS, newOn: valid ? raw : null };
}

/** Matches `PAGE_SIZE` in the repository. */
export const PAGE_SIZE = 50;

/**
 * Build the query string.
 *
 * Multi-value dimensions REPEAT the parameter rather than comma-separating.
 * `URLSearchParams.append` does this correctly and, more to the point, encodes
 * the value: an artist called "Earth, Wind & Fire" survives, where a
 * comma-joined string would arrive as three artists that match nothing and the
 * page would come back empty with no explanation.
 *
 * A null price bound is OMITTED rather than sent as an empty string, so "no
 * lower bound" and "a lower bound of zero" stay distinguishable.
 */
export function buildQuery(filters: BrowseFilters, offset: number, limit = PAGE_SIZE): string {
  const params = new URLSearchParams();
  const multi: ReadonlyArray<[string, string[]]> = [
    ["artist", filters.artists],
    ["platform", filters.platforms],
    ["type", filters.types],
    ["size", filters.sizes],
    ["color", filters.colors],
  ];
  for (const [name, values] of multi) {
    for (const value of values) params.append(name, value);
  }
  if (filters.priceMin !== null) params.set("price_min", String(filters.priceMin));
  if (filters.priceMax !== null) params.set("price_max", String(filters.priceMax));
  if (filters.dealsOnly) params.set("deals_only", "true");
  if (filters.newOn !== null) params.set("new_on", filters.newOn);
  params.set("sort", filters.sort);
  params.set("offset", String(offset));
  params.set("limit", String(limit));
  return params.toString();
}

export async function fetchListings(
  filters: BrowseFilters,
  offset = 0,
  limit = PAGE_SIZE,
): Promise<ListingsPage> {
  const res = await api.authedFetch(`/api/listings?${buildQuery(filters, offset, limit)}`);
  if (!res.ok) throw new Error(`Could not load listings (HTTP ${res.status})`);
  const body = (await res.json()) as Partial<ListingsPage>;
  // A 200 with the wrong shape is not success. Without this the page reads
  // `items` off an object that has none, hands `undefined` to `.map`, and dies
  // on a white screen with a console trace -- where an error banner would have
  // said what happened. `authedFetch` already rejects a non-JSON 2xx; this is
  // the same guard one level in, for JSON that is not THIS json.
  if (!Array.isArray(body.items) || typeof body.total !== "number" || !body.facets) {
    throw new Error("The listings API returned an unexpected response.");
  }
  return body as ListingsPage;
}

/** Star a listing. The server keys off the URL, so the whole listing goes. */
export async function save(item: Listing): Promise<void> {
  const res = await api.authedFetch("/api/saved", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(toWriteBody(item)),
  });
  if (!res.ok) throw new Error(`Could not save (HTTP ${res.status})`);
}

export async function unsave(key: string): Promise<void> {
  const res = await api.authedFetch(`/api/saved/${encodeURIComponent(key)}`, { method: "DELETE" });
  if (res.status === 404) {
    throw new Error("That item is no longer saved. Reload to see the current list.");
  }
  if (!res.ok) throw new Error(`Could not un-save (HTTP ${res.status})`);
}

/**
 * Hide a listing.
 *
 * The whole listing goes, not just the key, and that is load-bearing rather
 * than convenient: `common_utils.merch_dismiss_patterns` learns which keywords
 * to auto-filter from the TITLE and ARTIST of past dismissals. A dismissal
 * recorded without them teaches it nothing, and nothing would fail to say so.
 */
export async function dismiss(item: Listing): Promise<void> {
  const res = await api.authedFetch("/api/dismissed", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(toWriteBody(item)),
  });
  if (!res.ok) throw new Error(`Could not dismiss (HTTP ${res.status})`);
}

/** The fields both write endpoints accept. Derived state (`saved`, `is_new`,
 * `discount`) is deliberately not sent: it is the server's to compute, and
 * echoing it back would let a stale tab overwrite a fresher answer. */
export function toWriteBody(item: Listing): Record<string, unknown> {
  return {
    url: item.url,
    title: item.title,
    artist: item.artist,
    platform: item.platform,
    price: item.price,
    type: item.type,
    size: item.size,
    color: item.color,
    image_url: item.image_url,
  };
}

/**
 * The metadata line: price, size, type, colour, platform.
 *
 * Price is OMITTED when a discount is present, because the deal line above it
 * already shows the price alongside what it is a discount from, and showing it
 * twice makes the card look like it has two prices.
 * "Unknown" size and colour are dropped rather than printed: they mean
 * the scraper could not tell, and printing them states a non-fact.
 */
export function metaLine(item: Listing): string {
  const parts: string[] = [];
  const price = formatPrice(item.price);
  if (price !== null && item.discount === null) parts.push(price);
  if (item.size && item.size !== "Unknown") parts.push(item.size);
  if (item.type) parts.push(item.type);
  if (item.color && item.color !== "Unknown") parts.push(item.color);
  if (item.platform) parts.push(platformLabel(item.platform));
  return parts.join(" | ");
}

/** The green deal line, or null. `$18 · was $45 typical (60% below)`. */
export function dealLine(item: Listing): string | null {
  if (item.discount === null || item.baseline === null) return null;
  const price = formatPrice(item.price);
  if (price === null) return null;
  return `${price} · was ${formatPrice(item.baseline)} typical (${Math.round(item.discount * 100)}% below)`;
}

/**
 * "Not seen in N days", from 2 upward, or null.
 *
 * The threshold is 2 because anything older has already been dropped as stale,
 * so this only ever fires in the narrow band between "seen recently" and
 * "gone". Showing it at 0 or 1 day would put a worrying caption on every card.
 */
export function staleNote(item: Listing): string | null {
  if (item.days_since_seen === null || item.days_since_seen < 2) return null;
  return `Not seen in ${item.days_since_seen} days`;
}

/** Falls back to the key when a listing has no title. */
export function displayTitle(item: Listing): string {
  return item.title || item.key;
}

/** `Showing 50 of 1971 listings`. The second number is the filtered total, so
 * it is what tells you whether the filter did anything. */
export function summaryLine(shown: number, total: number): string {
  return `Showing ${shown} of ${total} listing${total === 1 ? "" : "s"}`;
}

/** How many remain behind the Load-more button, never negative. */
export function remaining(shown: number, total: number): number {
  return Math.max(0, total - shown);
}


/**
 * How many dimensions the user has narrowed, for the collapsed filter summary.
 *
 * Counts DIMENSIONS, not selections: "3 filters" for three chosen artists reads
 * as three separate constraints when it is one. Sort is deliberately excluded
 * because it never hides anything, and a collapsed panel claiming a filter when
 * only the order changed would send you hunting for merch that was never
 * missing.
 *
 * A price bound counts once whichever end is set, for the same reason.
 */
export function activeFilterCount(filters: BrowseFilters): number {
  return [
    filters.artists.length > 0,
    filters.platforms.length > 0,
    filters.types.length > 0,
    filters.sizes.length > 0,
    filters.colors.length > 0,
    filters.priceMin !== null || filters.priceMax !== null,
    filters.dealsOnly,
    filters.newOn !== null,
  ].filter(Boolean).length;
}
